/**
 * Moteur commun des tutoriels : une seule configuration driver.js pour tout le site.
 *
 * Les étapes dont la cible est absente ou invisible sont écartées au lancement. C'est ce
 * qui permet un jeu d'étapes unique malgré les rendus différents — entrée de menu propre
 * à un rôle, tableau desktop contre cartes mobiles, section vide.
 */
import { driver, type Config, type DriveStep, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import i18n from "../../i18n/i18n";
import { releaseTour } from "./tourQueue";

export type TourStep = DriveStep;

/** Montée ET de taille non nulle : `hidden md:block` ou un tiroir fermé ne comptent pas. */
export const isUsableTarget = (selector: string): boolean => {
  const element = document.querySelector(selector);
  if (!(element instanceof HTMLElement)) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/** Étape ancrée sur la première cible visible, `null` si aucune ne l'est. */
export const anchoredStep = (
  selectors: string[],
  step: Omit<TourStep, "element">
): TourStep | null => {
  const element = selectors.find(isUsableTarget);
  return element ? { ...step, element } : null;
};

export const compactSteps = (steps: (TourStep | null)[]): TourStep[] =>
  steps.filter((step): step is TourStep => step !== null);

/**
 * `data.keepWhenMissing` est l'échappatoire des cibles qui n'apparaissent qu'en cours de
 * route : le tutoriel du dashboard ouvre lui-même le mode personnalisation pour montrer
 * des boutons qui n'existent pas avant.
 */
const usableSteps = (steps: TourStep[]): TourStep[] =>
  steps.filter(
    (step) =>
      step.data?.keepWhenMissing === true ||
      typeof step.element !== "string" ||
      isUsableTarget(step.element)
  );

/**
 * Lance un tour, ou renvoie `null` si aucune étape n'est exploitable — l'appelant ne
 * marque alors rien comme vu et le tour se représentera.
 */
export const startTour = (
  steps: TourStep[],
  overrides: Partial<Config> = {}
): Driver | null => {
  const usable = usableSteps(steps);
  if (usable.length === 0) {
    releaseTour();
    return null;
  }

  const { onDestroyed, ...rest } = overrides;

  const instance = driver({
    showProgress: true,
    allowClose: true,
    disableActiveInteraction: true,
    overlayColor: "#1E1E1E",
    overlayOpacity: 0.6,
    stagePadding: 6,
    stageRadius: 12,
    nextBtnText: i18n.t("tour-next"),
    prevBtnText: i18n.t("tour-prev"),
    doneBtnText: i18n.t("tour-done"),
    progressText: "{{current}} / {{total}}",
    popoverClass: "copernic-tour-popover",
    ...rest,
    steps: usable,
    onDestroyed: (element, step, opts) => {
      releaseTour();
      onDestroyed?.(element, step, opts);
    },
  });

  instance.drive();
  return instance;
};
